import { useState, useRef, useEffect, useCallback } from "react";

/**
 * Shared Toast component — fixed bottom-right notification.
 *
 * Usage:
 *   const { toast, showToast } = useToast();        // default 3000ms
 *   const { toast, showToast } = useToast(2500);    // custom duration
 *
 *   showToast("Saved");                             // success (default)
 *   showToast("Something went wrong", "error");     // error
 *
 *   <Toast toast={toast} />
 */
export function useToast(duration = 3000) {
  const [toast, setToast] = useState(null);
  const timerRef = useRef(null);

  const showToast = useCallback(
    (message, type = "success", action = null) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      setToast({ message, type, action });
      // Errors stay visible longer (5s); undo toasts stay for 5.5s so the undo window fits
      const ms = action ? Math.max(duration, 5500) : type === "error" ? Math.max(duration, 5000) : duration;
      timerRef.current = setTimeout(() => setToast(null), ms);
    },
    [duration]
  );

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  const dismissToast = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setToast(null);
  }, []);

  return { toast, showToast, dismissToast };
}

export default function Toast({ toast, onDismiss }) {
  if (!toast) return null;
  const isError = toast.type === "error";
  const hasAction = !!toast.action;
  return (
    <div
      role="alert"
      onClick={hasAction ? undefined : onDismiss}
      title={hasAction ? undefined : "Click to dismiss"}
      style={{
        position: "fixed", bottom: 28, right: 28, zIndex: 9999,
        padding: "10px 16px", borderRadius: 10, fontSize: 13, fontWeight: 600,
        background: isError ? "var(--danger-bg)" : "#f0fdf4",
        border: `1px solid ${isError ? "#fca5a5" : "#86efac"}`,
        color: isError ? "var(--danger-text)" : "var(--success-text)",
        boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
        display: "flex", alignItems: "center", gap: 10,
        animation: "fadeInUp 0.2s ease",
        cursor: hasAction ? "default" : "pointer",
        userSelect: "none",
      }}
    >
      <span>{isError ? "⚠" : "✓"} {toast.message}</span>
      {hasAction && (
        <button
          onClick={(e) => { e.stopPropagation(); toast.action.onClick(); onDismiss(); }}
          style={{
            background: "none", border: "none", cursor: "pointer", padding: "2px 6px",
            fontWeight: 700, fontSize: 13, textDecoration: "underline",
            color: "inherit", borderRadius: 4,
          }}
        >
          {toast.action.label}
        </button>
      )}
      {hasAction && (
        <button
          onClick={(e) => { e.stopPropagation(); onDismiss(); }}
          style={{ background: "none", border: "none", cursor: "pointer", padding: "2px 4px", opacity: 0.6, fontSize: 14, color: "inherit", lineHeight: 1 }}
          title="Dismiss"
        >
          ✕
        </button>
      )}
    </div>
  );
}
